import { Component } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BackIconButton from '../common/BackIconButton';
import Drawer from '../Drawer/Drawer';
import Footer from '../Sections/Footer';
import PinGroupedField from './PinGroupedField';
import PinRecoveryScreen from './PinRecoveryScreen';
import { withTranslation } from '../../hoc/withTranslation';
import {
	getEntityThemedCtaButtonSx,
	getOpaqueDialogSurface,
} from '../../utils/ctaContrast.utils';
import { keyboardFocusVisibleSx } from '../../utils/a11y.utils';
import {
	PIN_SETUP_TITLE_ID,
	PIN_CONFIRM_TITLE_ID,
	PIN_SUCCESS_TITLE_ID,
	PIN_FAILURE_TITLE_ID,
} from '../../utils/pageHeading.utils';
import {
	captureMfaPinSheetOpened,
	captureMfaPinSubmitted,
	captureMfaPinResult,
	captureMfaPinRecoveryStarted,
} from '../../utils/analytics/posthog.utils';
import {
	getLastMfaWsError,
	clearLastMfaWsError,
	getMfaWsFactorData,
	isMfaFactorEnrolled,
} from '../../utils/mfaWsSniffer.utils';

const PIN_STEPS = {
	SETUP: 'setup',
	CONFIRM: 'confirm',
	ENTER: 'enter',
	SUCCESS: 'success',
	FAILURE: 'failure',
};

const FAILURE_TYPES = {
	MISMATCH: 'mismatch',
	ERROR: 'error',
};

const getExactErrorMessage = (step, fallback = '') => {
	const wsErr = getLastMfaWsError();
	if (wsErr && typeof wsErr === 'string' && wsErr.trim()) {
		return wsErr.trim();
	}

	const wsFactorData = typeof window !== 'undefined' ? window._lastMfaWsFactorData : null;
	if (wsFactorData) {
		if (typeof wsFactorData.error === 'string' && wsFactorData.error.trim()) return wsFactorData.error.trim();
		if (typeof wsFactorData.errorMessage === 'string' && wsFactorData.errorMessage.trim()) return wsFactorData.errorMessage.trim();
		if (
			typeof wsFactorData.message === 'string' &&
			wsFactorData.message.trim() &&
			wsFactorData.message !== 'MFA authentication failed' &&
			wsFactorData.message !== 'Authentication failed'
		) {
			return wsFactorData.message.trim();
		}
	}

	const wsPayload = typeof window !== 'undefined' ? window._lastMfaWsPayload : null;
	if (wsPayload) {
		if (typeof wsPayload.error === 'string' && wsPayload.error.trim()) return wsPayload.error.trim();
		if (typeof wsPayload.errorMessage === 'string' && wsPayload.errorMessage.trim()) return wsPayload.errorMessage.trim();
		if (
			typeof wsPayload.message === 'string' &&
			wsPayload.message.trim() &&
			wsPayload.message !== 'MFA authentication failed' &&
			wsPayload.message !== 'Authentication failed'
		) {
			return wsPayload.message.trim();
		}
	}

	if (!step) return fallback;
	const valErr = step.validationError;
	const resErr = step.result?.error;
	const errObj = step.error;
	const factorData =
		step.factorData ||
		valErr?.factorData ||
		resErr?.factorData ||
		resErr?.details?.factorData;

	if (factorData) {
		if (typeof factorData.error === 'string' && factorData.error.trim()) return factorData.error.trim();
		if (typeof factorData.errorMessage === 'string' && factorData.errorMessage.trim()) return factorData.errorMessage.trim();
		if (typeof factorData.message === 'string' && factorData.message.trim()) return factorData.message.trim();
	}

	if (typeof valErr === 'string' && valErr.trim()) return valErr.trim();
	if (valErr?.error && typeof valErr.error === 'string' && valErr.error.trim()) return valErr.error.trim();
	if (
		valErr?.message &&
		typeof valErr.message === 'string' &&
		valErr.message.trim() &&
		valErr.message !== 'MFA authentication failed' &&
		valErr.message !== 'Authentication failed'
	) {
		return valErr.message.trim();
	}

	if (typeof resErr === 'string' && resErr.trim()) return resErr.trim();
	if (resErr?.error && typeof resErr.error === 'string' && resErr.error.trim()) return resErr.error.trim();
	if (
		resErr?.message &&
		typeof resErr.message === 'string' &&
		resErr.message.trim() &&
		resErr.message !== 'MFA authentication failed' &&
		resErr.message !== 'Authentication failed'
	) {
		return resErr.message.trim();
	}

	if (typeof errObj === 'string' && errObj.trim()) return errObj.trim();
	if (errObj?.error && typeof errObj.error === 'string' && errObj.error.trim()) return errObj.error.trim();
	if (
		errObj?.message &&
		typeof errObj.message === 'string' &&
		errObj.message.trim() &&
		errObj.message !== 'MFA authentication failed' &&
		errObj.message !== 'Authentication failed'
	) {
		return errObj.message.trim();
	}

	if (step.result?.message && typeof step.result.message === 'string' && step.result.message.trim() && step.result.message !== 'MFA authentication failed' && step.result.message !== 'Authentication failed') {
		return step.result.message.trim();
	}

	if (step.message && typeof step.message === 'string' && step.message.trim() && step.message !== 'MFA authentication failed' && step.message !== 'Authentication failed') {
		return step.message.trim();
	}

	if (valErr?.message && typeof valErr.message === 'string' && valErr.message.trim()) return valErr.message.trim();
	if (resErr?.message && typeof resErr.message === 'string' && resErr.message.trim()) return resErr.message.trim();

	if (valErr?.code && typeof valErr.code === 'string') return valErr.code;
	if (resErr?.code && typeof resErr.code === 'string') return resErr.code;

	return fallback;
};

class PinSetupFlow extends Component {
	constructor(props) {
		super(props);
		const isVerify = this.getIsVerifyMode(props);
		this.state = {
			step: isVerify ? PIN_STEPS.ENTER : PIN_STEPS.SETUP,
			pin: '',
			confirmPin: '',
			enterPin: '',
			failureType: FAILURE_TYPES.MISMATCH,
			errorMessage: '',
			verificationError: '',
			isSubmitting: false,
		};
		this._successTimer = null;
	}

	componentDidMount() {
		if (this.props.open) {
			captureMfaPinSheetOpened({
				mode: this.getIsVerifyMode() ? 'verification' : 'enrollment',
			});
		}
	}

	getIsVerifyMode = (props = this.props) => {
		const wsFactorData = getMfaWsFactorData();
		const wsEnrolled = isMfaFactorEnrolled();
		const step = window._currentMfaStep;

		const backendEnrolled =
			step?.factorData?.enrolled !== undefined
				? step.factorData.enrolled
				: step?.inputSpec?.enrolled !== undefined
					? step.inputSpec.enrolled
					: wsEnrolled !== undefined
						? wsEnrolled
						: wsFactorData?.enrolled !== undefined
							? wsFactorData.enrolled
							: undefined;

		if (backendEnrolled === true) return true;
		if (backendEnrolled === false) return false;
		if (step?.requirement === 'verification') return true;
		if (step?.requirement === 'enrollment') return false;

		if (props.isPinRegistered === true) return true;
		if (props.isPinRegistered === false) return false;
		if (props.mode === 'enter' || props.mode === 'verify') return true;
		if (props.mode === 'setup') return false;

		return Boolean(global.isPinRegistered);
	};

	componentDidUpdate(prevProps) {
		if (
			(!prevProps.open && this.props.open) ||
			(this.props.open && prevProps.mode !== this.props.mode) ||
			(this.props.open && prevProps.isPinRegistered !== this.props.isPinRegistered)
		) {
			this.resetFlow();
			captureMfaPinSheetOpened({
				mode: this.getIsVerifyMode() ? 'verification' : 'enrollment',
			});
		}
	}

	componentWillUnmount() {
		this.clearSuccessTimer();
	}

	clearSuccessTimer = () => {
		if (this._successTimer) {
			clearTimeout(this._successTimer);
			this._successTimer = null;
		}
	};

	resetFlow = () => {
		this.clearSuccessTimer();
		clearLastMfaWsError();
		const isVerify = this.getIsVerifyMode();
		this.setState({
			step: isVerify ? PIN_STEPS.ENTER : PIN_STEPS.SETUP,
			pin: '',
			confirmPin: '',
			enterPin: '',
			failureType: FAILURE_TYPES.MISMATCH,
			errorMessage: '',
			verificationError: '',
			isSubmitting: false,
		});
	};

	handleClose = () => {
		if (this.state.isSubmitting || this.state.step === PIN_STEPS.SUCCESS) {
			console.log(
				'[PinSetupFlow] Ignoring user close while verifying or on success',
			);
			return;
		}
		this.resetFlow();
		this.props.onClose();
	};

	handleBack = () => {
		const { step } = this.state;
		const isVerify = this.getIsVerifyMode();

		// If user has already done PIN setup (returning user / login flow), never go back to create PIN!
		if (isVerify) {
			if (step === PIN_STEPS.FAILURE || step === PIN_STEPS.SUCCESS) {
				this.setState({
					step: PIN_STEPS.ENTER,
					enterPin: '',
					isSubmitting: false,
				});
				return;
			}
			// From enter step, back closes the drawer to return to the previous screen
			this.handleClose();
			return;
		}

		if (step === PIN_STEPS.CONFIRM) {
			this.setState({ step: PIN_STEPS.SETUP, pin: '', confirmPin: '' });
			return;
		}
		if (step === PIN_STEPS.SUCCESS || step === PIN_STEPS.FAILURE) {
			this.resetFlow();
		}
	};

	handleSetupComplete = (pin) => {
		this.setState({ pin, step: PIN_STEPS.CONFIRM, confirmPin: '' });
	};

	handleConfirmComplete = async (confirmPin) => {
		const { pin } = this.state;
		this.setState({ confirmPin, isSubmitting: true });
		captureMfaPinSubmitted({ mode: 'enrollment' });

		if (pin !== confirmPin) {
			captureMfaPinResult({
				success: false,
				mode: 'enrollment',
				errorCode: 'pinMismatch',
				errorMessage: 'PINs do not match',
			});
			this.setState({
				step: PIN_STEPS.FAILURE,
				failureType: FAILURE_TYPES.MISMATCH,
				isSubmitting: false,
			});
			return;
		}

		try {
			const success = await this.props.onSubmitPin?.(pin);
			if (success === false) {
				const receivedError = getExactErrorMessage(
					window._currentMfaStep,
					'Unable to create PIN. Please try again.',
				);

				captureMfaPinResult({
					success: false,
					mode: 'enrollment',
					errorCode:
						window._currentMfaStep?.validationError?.code ||
						window._currentMfaStep?.result?.error?.code ||
						'pinEnrollmentFailed',
					errorMessage: receivedError,
				});
				this.setState({
					step: PIN_STEPS.FAILURE,
					failureType: FAILURE_TYPES.ERROR,
					errorMessage: receivedError,
					isSubmitting: false,
				});
				return;
			}

			captureMfaPinResult({
				success: true,
				mode: 'enrollment',
			});
			this.setState({ step: PIN_STEPS.SUCCESS, isSubmitting: false });
			this._successTimer = setTimeout(() => {
				this.resetFlow();
				this.props.onClose();
				this.props.onSuccess?.();
			}, 2000);
		} catch (e) {
			const receivedError = getExactErrorMessage(
				window._currentMfaStep,
				e?.message || 'Unable to create PIN. Please try again.',
			);

			captureMfaPinResult({
				success: false,
				mode: 'enrollment',
				errorCode: 'pinEnrollmentException',
				errorMessage: receivedError,
			});
			this.setState({
				step: PIN_STEPS.FAILURE,
				failureType: FAILURE_TYPES.ERROR,
				errorMessage: receivedError,
				isSubmitting: false,
			});
		}
	};

	handleTryAgain = () => {
		clearLastMfaWsError();
		const isVerify = this.getIsVerifyMode();
		this.setState({
			step: isVerify ? PIN_STEPS.ENTER : PIN_STEPS.SETUP,
			pin: '',
			confirmPin: '',
			enterPin: '',
			errorMessage: '',
			verificationError: '',
			isSubmitting: false,
		});
	};

	handleEnterComplete = async (enterPin) => {
		this.setState({ enterPin, isSubmitting: true, verificationError: '' });
		captureMfaPinSubmitted({ mode: 'verification' });
		try {
			const success = await this.props.onSubmitPin?.(enterPin);
			if (success === false) {
				const step = window._currentMfaStep;
				const valErr = step?.validationError;
				const resErr = step?.result?.error;
				const attempts = valErr?.attemptsRemaining;
				const isLocked =
					attempts === 0 ||
					valErr?.code === 'accountLocked' ||
					valErr?.code === 'maxAttemptsExceeded' ||
					valErr?.code === 'MAX_ATTEMPTS_EXCEEDED' ||
					valErr?.errorCode === 'MAX_ATTEMPTS_EXCEEDED' ||
					valErr?.error === 'MAX_ATTEMPTS_EXCEEDED' ||
					step?.factorStatus === 'FAILED';

				const receivedError = getExactErrorMessage(
					step,
					attempts !== undefined && attempts !== null
						? `Incorrect PIN. ${attempts} attempt${attempts === 1 ? '' : 's'} remaining.`
						: 'Incorrect PIN. Please try again.',
				);

				captureMfaPinResult({
					success: false,
					mode: 'verification',
					errorCode:
						valErr?.code || resErr?.code || (isLocked ? 'accountLocked' : 'invalidPin'),
					attemptsRemaining: attempts,
					isLocked,
				});

				if (isLocked) {
					captureMfaPinRecoveryStarted({
						source: 'pin_sheet',
						reason: 'max_attempts_exceeded',
					});
					this.setState({
						step: PIN_STEPS.FAILURE,
						failureType: FAILURE_TYPES.LOCKED,
						errorMessage: receivedError,
						isSubmitting: false,
					});
					return;
				}

				if (
					step?.action === 'complete' &&
					step.result?.status === 'failed'
				) {
					this.setState({
						step: PIN_STEPS.FAILURE,
						failureType: FAILURE_TYPES.ERROR,
						errorMessage: receivedError,
						isSubmitting: false,
					});
					return;
				}

				this.setState({
					enterPin: '',
					isSubmitting: false,
					verificationError: receivedError,
				});
				return;
			}
			captureMfaPinResult({
				success: true,
				mode: 'verification',
			});
			this.setState({ step: PIN_STEPS.SUCCESS, isSubmitting: false });
			this._successTimer = setTimeout(() => {
				this.resetFlow();
				this.props.onClose();
				this.props.onSuccess?.();
			}, 2000);
		} catch (e) {
			const receivedError = getExactErrorMessage(
				window._currentMfaStep,
				e?.message || 'Authentication failed. Please try again.',
			);

			captureMfaPinResult({
				success: false,
				mode: 'verification',
				errorCode: 'pinVerificationException',
				errorMessage: receivedError,
			});
			this.setState({
				step: PIN_STEPS.FAILURE,
				failureType: FAILURE_TYPES.ERROR,
				errorMessage: receivedError,
				isSubmitting: false,
			});
		}
	};

	renderHeader = (isDark, showBack, showClose = true) => {
		const isLocked = Boolean(
			this.state.isSubmitting || this.state.step === PIN_STEPS.SUCCESS,
		);
		const allowClose = showClose && !isLocked;
		const allowBack = showBack && !isLocked;
		const iconBg = isDark ? '#2D2D2D' : '#F2F4F7';
		const iconColor = isDark ? '#fff' : '#444';

		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					minHeight: '2.75rem',
					mb: '1.5rem',
				}}
			>
				<Box sx={{ width: '2.75rem', flexShrink: 0 }}>
					{allowBack && (
						<BackIconButton
							onClick={this.handleBack}
							sx={{
								width: '2.25rem',
								height: '2.25rem',
								backgroundColor: iconBg,
								color: iconColor,
								'&:hover': { backgroundColor: iconBg },
							}}
							iconSx={{ fontSize: '1.25rem' }}
						/>
					)}
				</Box>
				<Box
					sx={{
						width: '2.75rem',
						flexShrink: 0,
						display: 'flex',
						justifyContent: 'flex-end',
					}}
				>
					{allowClose && (
						<IconButton
							aria-label={this.props.t('common.buttons.close')}
							onClick={this.handleClose}
							disableRipple
							sx={{
								width: '2.25rem',
								height: '2.25rem',
								backgroundColor: iconBg,
								color: iconColor,
								'&:hover': { backgroundColor: iconBg },
								...keyboardFocusVisibleSx,
							}}
						>
							<CloseIcon sx={{ fontSize: '1.125rem' }} />
						</IconButton>
					)}
				</Box>
			</Box>
		);
	};

	renderSetupConfirm = (isDark, primaryColor) => {
		const { t, customDetails } = this.props;
		const { step, pin, confirmPin, enterPin, isSubmitting } = this.state;
		const isEnter = step === PIN_STEPS.ENTER;
		const isConfirm = step === PIN_STEPS.CONFIRM;
		const titleId = isEnter
			? PIN_SETUP_TITLE_ID
			: isConfirm
				? PIN_CONFIRM_TITLE_ID
				: PIN_SETUP_TITLE_ID;
		const wsFactorData = getMfaWsFactorData();
		const pinLength = Number(
			wsFactorData?.pinLength ||
			window._currentMfaStep?.factorData?.pinLength ||
			window._currentMfaStep?.inputSpec?.pinLength ||
			this.props.pinLength ||
			4,
		);
		const title = isEnter
			? t('pinSetup.enterTitle', { pinLength }) ||
				`Enter your ${pinLength}-digit PIN`
			: isConfirm
				? t('pinSetup.confirmTitle', { pinLength }) ||
					`Confirm your ${pinLength}-digit PIN`
				: t('pinSetup.setupTitle', { pinLength }) ||
					`Setup a ${pinLength}-digit PIN`;

		return (
			<>
				{this.renderHeader(isDark, isConfirm && !isSubmitting, !isSubmitting)}
				<Typography
					id={titleId}
					component="h2"
					sx={{
						textAlign: 'center',
						fontWeight: 600,
						fontSize: '1rem',
						lineHeight: 1.4,
						mb: '2rem',
						fontFamily: customDetails?.fontFamily,
						color: isDark ? '#fff' : '#111',
					}}
				>
					{title}
				</Typography>
				<Box sx={{ display: 'flex', justifyContent: 'center', pb: '1rem' }}>
					{isEnter ? (
						<PinGroupedField
							key="pin-enter"
							pinLength={pinLength}
							value={enterPin}
							error={Boolean(this.state.verificationError)}
							helperText={this.state.verificationError}
							onChange={(value) =>
								this.setState({ enterPin: value, verificationError: '' })
							}
							onComplete={this.handleEnterComplete}
							primaryColor={primaryColor}
							fontFamily={customDetails?.fontFamily}
							customDetails={customDetails}
							labelId={titleId}
							autoFocus
							focusKey="enter"
						/>
					) : isConfirm ? (
						<PinGroupedField
							key="pin-confirm"
							pinLength={pinLength}
							value={confirmPin}
							onChange={(value) => this.setState({ confirmPin: value })}
							onComplete={this.handleConfirmComplete}
							primaryColor={primaryColor}
							fontFamily={customDetails?.fontFamily}
							customDetails={customDetails}
							labelId={titleId}
							autoFocus
							focusKey="confirm"
						/>
					) : (
						<PinGroupedField
							key="pin-setup"
							pinLength={pinLength}
							value={pin}
							onChange={(value) => this.setState({ pin: value })}
							onComplete={this.handleSetupComplete}
							primaryColor={primaryColor}
							fontFamily={customDetails?.fontFamily}
							customDetails={customDetails}
							labelId={titleId}
							autoFocus
							focusKey="setup"
						/>
					)}
				</Box>
				{isSubmitting && (
					<Typography
						sx={{
							textAlign: 'center',
							fontSize: '0.875rem',
							color: isDark ? '#B0B0B0' : '#666',
							fontFamily: customDetails?.fontFamily,
						}}
					>
						{isEnter
							? t('pinSetup.verifying') || 'Verifying PIN...'
							: t('pinSetup.creating')}
					</Typography>
				)}
			</>
		);
	};

	renderSuccess = (isDark, primaryColor) => {
		const { t, customDetails } = this.props;
		const isVerify = this.getIsVerifyMode();

		return (
			<>
				{this.renderHeader(isDark, false, false)}
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						py: '2rem',
						minHeight: '12rem',
					}}
				>
					<CheckCircleIcon
						aria-hidden
						sx={{
							fontSize: '3.5rem',
							color: customDetails?.successColor || '#22C55E',
							mb: '1rem',
						}}
					/>
					<Typography
						id={PIN_SUCCESS_TITLE_ID}
						component="h2"
						sx={{
							textAlign: 'center',
							fontWeight: 500,
							fontSize: '1rem',
							lineHeight: 1.45,
							fontFamily: customDetails?.fontFamily,
							color: isDark ? '#fff' : '#333',
						}}
					>
						{isVerify
							? t('pinSetup.verifySuccessTitle') || 'PIN verified successfully!'
							: t('pinSetup.successTitle') || 'PIN successfully created!'}
					</Typography>
				</Box>
			</>
		);
	};

	renderFailure = (isDark, primaryColor) => {
		const { t, customDetails } = this.props;
		const { failureType, step } = this.state;
		const isLocked = failureType === FAILURE_TYPES.LOCKED;

		if (isLocked) {
			return (
				<PinRecoveryScreen
					customDetails={customDetails}
					onClose={this.handleClose}
					onRetryBiometric={this.props.onRetryBiometric}
					hasBiometric={Boolean(this.props.onRetryBiometric)}
				/>
			);
		}
		const isEnter =
			step === PIN_STEPS.ENTER ||
			this.props.mode === 'enter' ||
			this.props.mode === 'verify' ||
			window._currentMfaStep?.inputSpec?.enrolled === true;
		const title = isLocked
			? t('pinSetup.lockedTitle') || 'Too many incorrect PIN attempts'
			: null;
		const message =
			this.state.errorMessage ||
			(isLocked
				? 'You’ve used all 3 PIN attempts. Contact support to regain access.'
				: failureType === FAILURE_TYPES.MISMATCH
					? 'PINs do not match. Please try again.'
					: isEnter
						? 'Incorrect PIN. Please try again.'
						: 'Unable to create PIN. Please try again.');

		const primaryButtonSx = getEntityThemedCtaButtonSx(customDetails, isDark, {
			textTransform: 'capitalize',
			fontSize: '0.875rem',
			border: `1px solid ${customDetails?.ctaBorderColor ?? primaryColor}`,
			borderRadius: customDetails?.borderRadius ?? '6px',
			fontFamily: customDetails?.fontFamily,
			boxShadow: 'none',
			mt: '1.5rem',
			...keyboardFocusVisibleSx,
			'&:hover': { boxShadow: 'none' },
		});

		const isVerify = this.getIsVerifyMode();
		return (
			<>
				{this.renderHeader(isDark, !isVerify)}
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						py: '1.5rem',
						minHeight: '10rem',
					}}
				>
					<CancelIcon
						aria-hidden
						sx={{
							fontSize: '3.5rem',
							color: customDetails?.errorColor || '#EF4444',
							mb: '1rem',
						}}
					/>
					{title && (
						<Typography
							component="h2"
							sx={{
								textAlign: 'center',
								fontWeight: 600,
								fontSize: '1.125rem',
								lineHeight: 1.4,
								mb: '0.5rem',
								fontFamily: customDetails?.fontFamily,
								color: isDark ? '#fff' : '#111',
							}}
						>
							{title}
						</Typography>
					)}
					<Typography
						id={PIN_FAILURE_TITLE_ID}
						component="p"
						sx={{
							textAlign: 'center',
							fontWeight: 500,
							fontSize: '0.9375rem',
							lineHeight: 1.45,
							maxWidth: '18rem',
							fontFamily: customDetails?.fontFamily,
							color: isDark ? '#B0B0B0' : '#666',
						}}
					>
						{message}
					</Typography>
					<Button
						variant="contained"
						size="large"
						fullWidth
						onClick={this.handleTryAgain}
						sx={primaryButtonSx}
					>
						{t('pinSetup.tryAgain')}
					</Button>
				</Box>
			</>
		);
	};

	renderContent = (isDark, primaryColor) => {
		const { step } = this.state;

		switch (step) {
			case PIN_STEPS.SUCCESS:
				return this.renderSuccess(isDark, primaryColor);
			case PIN_STEPS.FAILURE:
				return this.renderFailure(isDark, primaryColor);
			default:
				return this.renderSetupConfirm(isDark, primaryColor);
		}
	};

	getAriaTitleId = () => {
		const { step } = this.state;
		switch (step) {
			case PIN_STEPS.CONFIRM:
				return PIN_CONFIRM_TITLE_ID;
			case PIN_STEPS.SUCCESS:
				return PIN_SUCCESS_TITLE_ID;
			case PIN_STEPS.FAILURE:
				return PIN_FAILURE_TITLE_ID;
			default:
				return PIN_SETUP_TITLE_ID;
		}
	};

	renderDrawer = (isDark, primaryColor, contentBackground) => {
		const isLocked = Boolean(
			this.state.isSubmitting || this.state.step === PIN_STEPS.SUCCESS,
		);
		return (
			<Drawer
				open={this.props.open}
				setOpen={(open) => {
					if (!open && !isLocked) this.handleClose();
				}}
				anchor="bottom"
				height="auto"
				customDetails={this.props.customDetails}
				showCloseBtn={false}
				flushBottom
				ariaLabelledBy={this.getAriaTitleId()}
				paperBackground={contentBackground}
				disableClose={isLocked}
			>
				<Box
					sx={{
						backgroundColor: contentBackground,
						fontFamily: this.props.customDetails?.fontFamily,
						overflow: 'hidden',
					}}
				>
					<Box
						sx={{
							px: '1.5rem',
							pt: '1.25rem',
							pb: '1.5rem',
						}}
					>
						{this.renderContent(isDark, primaryColor)}
					</Box>
				</Box>
				<Footer
					{...(this.props.parentProps || {
						customDetails: this.props.customDetails,
					})}
				/>
			</Drawer>
		);
	};

	renderDialog = (isDark, primaryColor, contentBackground) => {
		const isLocked = Boolean(
			this.state.isSubmitting || this.state.step === PIN_STEPS.SUCCESS,
		);
		return (
			<Dialog
				open={this.props.open}
				onClose={(event, reason) => {
					if (isLocked) {
						return;
					}
					this.handleClose();
				}}
				aria-labelledby={this.getAriaTitleId()}
				maxWidth={false}
				disableEscapeKeyDown={isLocked}
				sx={{
					'& .MuiDialog-paper': {
						width: '100%',
						maxWidth: '22rem',
						borderRadius: '1.5rem',
						overflow: 'hidden',
						margin: '1rem',
						backgroundColor: contentBackground,
					},
				}}
			>
				<Box
					sx={{
						px: '1.5rem',
						pt: '1.25rem',
						pb: '2rem',
						backgroundColor: contentBackground,
						fontFamily: this.props.customDetails?.fontFamily,
					}}
				>
					{this.renderContent(isDark, primaryColor)}
				</Box>
			</Dialog>
		);
	};

	render() {
		if (!this.props.open) return null;

		const isDark =
			this.props.customDetails?.darkTheme === true ||
			this.props.isDark === true;
		const primaryColor = this.props.customDetails?.primaryColor || '#3053D3';
		const contentBackground = getOpaqueDialogSurface(isDark);

		if (
			this.state.step === PIN_STEPS.FAILURE &&
			this.state.failureType === FAILURE_TYPES.LOCKED
		) {
			return (
				<Box
					sx={{
						position: 'fixed',
						inset: 0,
						zIndex: 1050,
						backgroundColor: isDark ? '#121212' : '#FFFFFF',
						overflowY: 'auto',
					}}
				>
					<PinRecoveryScreen
						customDetails={this.props.customDetails}
						onClose={this.handleClose}
						onRetryBiometric={this.props.onRetryBiometric}
						hasBiometric={Boolean(this.props.onRetryBiometric)}
					/>
				</Box>
			);
		}

		if (this.props.isMobile) {
			return this.renderDrawer(isDark, primaryColor, contentBackground);
		}

		return this.renderDialog(isDark, primaryColor, contentBackground);
	}
}

export default withTranslation(PinSetupFlow);
